import { FC, useState } from "react"
import { Button, Dialog, DialogActions, DialogContent, DialogHeader, useIntl } from "@metsooutotec/modes-react-components"
import { StoreResults } from "../../../../stores/stores-helper"
import linerMaterialStore from "features/provisioning/liner-material-library/stores/liner-material-store"
import { NavigationDeleteEntityOptions } from "components/utils/modal-helper"
import styles from "./liner-material-delete-modal.module.css"
import { LinerSpecification } from "../models/liner-material-library-models"

interface LinerMaterialDeleteProps {
    isOpen: boolean | undefined
    onClose: (params: NavigationDeleteEntityOptions) => void
    entity: LinerSpecification | undefined
}

const LinerMaterialDeleteModal: FC<LinerMaterialDeleteProps> = ({ isOpen, onClose, entity }) => {
    const { formatMessage } = useIntl()

    const [isLoading, setLoading] = useState(false)
    const [errorState, setErrorState] = useState<{
        isError: boolean
        errorMessage: string
    }>({
        isError: false,
        errorMessage: "",
    })

    if (!isOpen) return null
    if (!entity) return null

    const linerMaterial = entity as LinerSpecification

    const labels = {
        header: formatMessage("linerMaterialDelete.header"),
        content: `${formatMessage("linerMaterialDelete.content")} '${linerMaterial?.materialReference}'.`,
        cancel: formatMessage("modal.cancel"),
        confirm: formatMessage("modal.confirm"),
    }

    const handleConfirm = async (): Promise<void> => {
        setLoading(true)
        setErrorState({ isError: false, errorMessage: "" })

        try {
            const result: StoreResults = await linerMaterialStore.deleteEntity(linerMaterial?.id as string)

            if (!result.passed) {
                setErrorState({ isError: true, errorMessage: result.message })
                return
            }
            onClose({ deleted: true, entityName: linerMaterial?.materialReference })
        } catch (error) {
            console.error("Error deleting liner material:", error)
            setErrorState({
                isError: true,
                errorMessage: formatMessage("error.unexpected"),
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog open={isOpen} dimmer size='small'>
            <DialogHeader>{labels.header}</DialogHeader>

            {errorState.isError && <p className={styles.errorMessage}>{errorState.errorMessage}</p>}

            <DialogContent data-cy='dialog-content'>{labels.content}</DialogContent>

            <DialogActions>
                <Button disabled={isLoading} onClick={() => onClose({ deleted: false })} subtle>
                    {labels.cancel}
                </Button>
                <Button loading={isLoading} disabled={isLoading} onClick={handleConfirm} primary>
                    {labels.confirm}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default LinerMaterialDeleteModal
